import { useQueryClient } from '@tanstack/react-query';
import { RefreshCwIcon } from 'lucide-react';
import { FormattedMessage } from 'react-intl';

import { Button } from '@/components/ui/button';
import type { ScanProgress } from '@/hooks/use-scan-progress';
import { gcpServiceAccountQueries } from '@/lib/queries/gcpServiceAccount';
import { awsCredentialsQueries } from '@/lib/queries/integrations/cloud/aws/credentials';
import { awsRoleQueries } from '@/lib/queries/integrations/cloud/aws/roles';
import { k8sClustersQueries } from '@/lib/queries/integrations/containers/k8sClusters';
import { githubConfigQueries } from '@/lib/queries/integrations/scm/github';
import { terraformBackendBucketsQueries } from '@/lib/queries/integrations/terraformBackend/buckets';
import type { TranslationKeys } from '@/lib/translations/types';
import type { FileRouteTypes } from '@/routeTree.gen';

import { Integration } from './Integration';

export type IntegrationsData = {
  categoryTitle: TranslationKeys;
  items: {
    title: TranslationKeys;
    logoUrl: string;
    connected: boolean;
    available: boolean;
    to?: FileRouteTypes['to'];
    onClick?: () => void;
    pending?: boolean;
    warnings?: TranslationKeys[];
    donePercentage?: number;
    scanProgress?: ScanProgress;
    isSyncing?: boolean;
  }[];
}[];

interface Props {
  data: IntegrationsData;
  defaultProjectId: string;
}

export const Integrations = ({
  data,
  defaultProjectId,
}: Props): JSX.Element => {
  const queryClient = useQueryClient();

  return (
    <>
      <div className="flex flex-row items-baseline">
        <h2 className="text-2xl mt-4">
          <FormattedMessage id="integrations.subtitle" />
        </h2>
        <Button
          variant="ghost"
          className="ml-2 h-8 w-8 p-0"
          onClick={() => {
            // TODO - Refactor this, this kind of listing isnt scalable
            queryClient.invalidateQueries(
              terraformBackendBucketsQueries.list(defaultProjectId),
            );
            queryClient.invalidateQueries(
              awsCredentialsQueries.list(defaultProjectId),
            );
            queryClient.invalidateQueries(
              awsRoleQueries.list(defaultProjectId),
            );
            queryClient.invalidateQueries(
              gcpServiceAccountQueries.list(defaultProjectId),
            );
            queryClient.invalidateQueries(
              githubConfigQueries.list(defaultProjectId),
            );
            queryClient.invalidateQueries(
              k8sClustersQueries.list(defaultProjectId),
            );
          }}
        >
          <RefreshCwIcon className="h-4 w-4" />
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-8 md:gap-2 mt-4 lg:gap-4">
        {data.map(({ categoryTitle, items }) => (
          <div
            className="flex flex-col grow basis-0 max-w-[256px] gap-2"
            key={categoryTitle}
          >
            <h3 className="text-sm xl:text-lg lg:text-md whitespace-nowrap">
              <FormattedMessage id={categoryTitle} />
            </h3>
            {items.map(
              ({
                title,
                logoUrl,
                connected,
                available,
                to,
                onClick,
                pending,
                warnings,
                donePercentage,
                scanProgress,
                isSyncing,
              }) => (
                <Integration
                  logoUrl={logoUrl}
                  connected={connected}
                  available={available}
                  title={title}
                  onClick={onClick}
                  to={to}
                  key={title}
                  pending={pending}
                  warnings={warnings}
                  donePercentage={donePercentage}
                  scanProgress={scanProgress}
                  isSyncing={isSyncing}
                />
              ),
            )}
          </div>
        ))}
      </div>
    </>
  );
};
